import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, Edit, Camera, CheckCircle, Calendar,
  Clock, ChevronDown, ChevronUp, Search, DollarSign, Edit2, Download, 
  FileText, FileSpreadsheet, UserPlus, Settings, CircleDollarSign,
  ScanFace, Power, PowerOff, UserX, AlertCircle, Loader2, Play
} from 'lucide-react';
import FaceCapture from './FaceCapture';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Status badge component
const StatusBadge = ({ status, dateOfRelieving, daysUntilRelieve, lastWorkingDate }) => {
  const colors = {
    draft: 'bg-gray-100 text-gray-700 border-gray-300',
    active: 'bg-green-100 text-green-700 border-green-300',
    inactive: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    notice_period: 'bg-orange-100 text-orange-700 border-orange-300',
    relieved: 'bg-red-100 text-red-700 border-red-300'
  };
  const labels = {
    draft: 'Draft',
    active: 'Active',
    inactive: 'Inactive',
    notice_period: 'Notice Period',
    relieved: 'Relieved'
  };
  
  // Check if staff is in notice period based on effective_status or date_of_relieving
  const effectiveStatus = status === 'notice_period' ? 'notice_period' : status;
  
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors[effectiveStatus] || colors.draft}`}>
        {labels[effectiveStatus] || effectiveStatus}
      </span>
      {effectiveStatus === 'notice_period' && dateOfRelieving && (
        <span className="text-[10px] text-orange-600 font-medium">
          Relieve: {dateOfRelieving} ({daysUntilRelieve} days left)
        </span>
      )}
    </div>
  );
};

// Setup progress indicator
const SetupProgress = ({ staff }) => {
  const steps = [
    { key: 'basic', label: 'Basic Info', done: !!staff?.full_name },
    { key: 'shift', label: 'Shift', done: staff?.shift_configured },
    { key: 'salary', label: 'Salary', done: staff?.payroll_configured },
    { key: 'face', label: 'Face', done: staff?.face_enrolled }
  ];
  
  return (
    <div className="flex items-center gap-1 text-xs">
      {steps.map((step, idx) => (
        <React.Fragment key={step.key}>
          <div className={`flex items-center gap-1 ${step.done ? 'text-green-600' : 'text-gray-400'}`}>
            {step.done ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-current" />}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {idx < steps.length - 1 && <span className="text-gray-300">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

const StaffManagementV2 = ({ franchiseId, isAdmin = false }) => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configOptions, setConfigOptions] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [showSalaryDialog, setShowSalaryDialog] = useState(false);
  const [showFaceDialog, setShowFaceDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showRelieveDialog, setShowRelieveDialog] = useState(false);
  const [showPayrollDialog, setShowPayrollDialog] = useState(false);
  
  // Form states
  const [basicForm, setBasicForm] = useState({
    full_name: '', phone_number: '', role: 'Cashier', age: '', sex: 'Male',
    aadhaar_number: '', address: '', city: '', pincode: '', date_of_joining: ''
  });
  const [shiftForm, setShiftForm] = useState({
    shift_type: 'fixed', shift_start_time: '09:00', shift_end_time: '18:00',
    grace_minutes: 10, required_hours_per_day: 8
  });
  const [salaryForm, setSalaryForm] = useState({
    salary_type: 'monthly', salary_amount: '', overtime_allowed: false, ot_multiplier: 1.0
  });
  const [relieveDate, setRelieveDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Attendance & Payroll
  const [attendanceData, setAttendanceData] = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [payrollData, setPayrollData] = useState(null);

  // Calculate working hours in minutes from start and end time
  const calculateWorkingMinutes = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    let startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }
    return endMinutes - startMinutes;
  };

  useEffect(() => {
    fetchConfigOptions();
    fetchStaff();
  }, [franchiseId]);

  const fetchConfigOptions = async () => {
    try {
      const response = await axios.get(`${API}/staff/config/options`);
      setConfigOptions(response.data);
    } catch (error) {
      console.error('Error fetching config options:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/staff?franchise_id=${franchiseId}`, {
        headers: getAuthHeaders()
      });
      setStaffList(response.data.staff || []);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffAttendance = async (staffId) => {
    try {
      const response = await axios.get(
        `${API}/attendance?franchise_id=${franchiseId}&staff_id=${staffId}&start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`
      );
      setAttendanceData(response.data.attendance || []);
    } catch (error) {
      toast.error('Failed to load attendance');
    }
  };

  const fetchStaffSalary = async (staffId) => {
    try {
      const now = new Date();
      const response = await axios.get(
        `${API}/staff/${staffId}/salary?month=${now.getMonth() + 1}&year=${now.getFullYear()}`
      );
      setSalaryData(response.data);
    } catch (error) {
      console.error('Error fetching salary:', error);
    }
  };

  // Step 1: Create Staff (Basic Info)
  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/staff/v2`, {
        franchise_id: franchiseId,
        ...basicForm
      }, { headers: getAuthHeaders() });
      
      toast.success('Staff created! Now configure shift settings.');
      setShowAddDialog(false);
      setBasicForm({
        full_name: '', phone_number: '', role: 'Cashier', age: '', sex: 'Male',
        aadhaar_number: '', address: '', city: '', pincode: '', date_of_joining: ''
      });
      fetchStaff();
      
      // Auto-open shift dialog for new staff
      const newStaff = { staff_id: response.data.staff_id, ...basicForm };
      setSelectedStaff(newStaff);
      setShowShiftDialog(true);
    } catch (error) {
      const errMsg = error.response?.data?.detail?.errors || error.response?.data?.detail || 'Failed to create staff';
      toast.error(typeof errMsg === 'object' ? Object.values(errMsg).join(', ') : errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Configure Shift
  const handleConfigureShift = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    
    // Validate grace period for fixed shift
    if (shiftForm.shift_type === 'fixed') {
      const workingMinutes = calculateWorkingMinutes(shiftForm.shift_start_time, shiftForm.shift_end_time);
      const graceMinutes = parseInt(shiftForm.grace_minutes) || 0;
      
      if (graceMinutes <= 0) {
        toast.error('Grace period must be greater than 0 minutes');
        return;
      }
      if (graceMinutes >= workingMinutes) {
        toast.error(`Grace period must be less than working hours (${workingMinutes} minutes)`);
        return;
      }
    }
    
    // Validate required hours for flexible shift
    if (shiftForm.shift_type === 'flexible') {
      const reqHours = parseFloat(shiftForm.required_hours_per_day) || 0;
      if (reqHours <= 0 || reqHours > 24) {
        toast.error('Required hours must be greater than 0 and up to 24 hours');
        return;
      }
    }
    
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/shift`, shiftForm, {
        headers: getAuthHeaders()
      });
      toast.success('Shift configured! Now set salary.');
      setShowShiftDialog(false);
      fetchStaff();
      
      // Auto-open salary dialog
      setShowSalaryDialog(true);
    } catch (error) {
      const errMsg = error.response?.data?.detail?.errors || error.response?.data?.detail || 'Failed to configure shift';
      toast.error(typeof errMsg === 'object' ? Object.values(errMsg).join(', ') : errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Configure Salary
  const handleConfigureSalary = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/salary`, {
        ...salaryForm,
        salary_amount: parseFloat(salaryForm.salary_amount) || 0
      }, { headers: getAuthHeaders() });
      toast.success('Salary configured! Now enroll face.');
      setShowSalaryDialog(false);
      fetchStaff();
      
      // Auto-open face dialog
      setShowFaceDialog(true);
    } catch (error) {
      const errMsg = error.response?.data?.detail?.errors || error.response?.data?.detail || 'Failed to configure salary';
      toast.error(typeof errMsg === 'object' ? Object.values(errMsg).join(', ') : errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Enroll Face
  const handleFaceCapture = async (data) => {
    if (!selectedStaff) return;
    setLoading(true);
    try {
      const response = await axios.put(`${API}/staff/${selectedStaff.staff_id}/face`, {
        face_descriptor: data.descriptor,
        profile_photo: data.image
      }, { headers: getAuthHeaders() });
      
      toast.success('Face enrolled successfully!');
      setShowFaceDialog(false);
      fetchStaff();
      
      if (response.data.can_activate) {
        // Auto-open activation dialog
        setShowStatusDialog(true);
      } else {
        toast.info(`Missing: ${response.data.missing_requirements?.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to enroll face');
    } finally {
      setLoading(false);
    }
  };

  // Activate Staff
  const handleActivateStaff = async () => {
    if (!selectedStaff) return;
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/status`, {
        status: 'active'
      }, { headers: getAuthHeaders() });
      toast.success('Staff activated! They can now mark attendance.');
      setShowStatusDialog(false);
      fetchStaff();
    } catch (error) {
      const errMsg = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to activate';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Deactivate Staff
  const handleDeactivateStaff = async () => {
    if (!selectedStaff) return;
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/status`, {
        status: 'inactive'
      }, { headers: getAuthHeaders() });
      toast.success('Staff deactivated');
      setShowStatusDialog(false);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to deactivate');
    } finally {
      setLoading(false);
    }
  };

  // Set Notice Period (relieve date in future)
  const handleSetNoticePeriod = async () => {
    if (!selectedStaff) return;
    setLoading(true);
    try {
      // Check if relieve date is in the future
      const today = new Date().toISOString().split('T')[0];
      if (relieveDate <= today) {
        toast.error('Relieve date must be in the future for notice period');
        setLoading(false);
        return;
      }
      
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/status`, {
        status: 'notice_period',
        date_of_relieving: relieveDate
      }, { headers: getAuthHeaders() });
      toast.success(`Staff put on notice period. Last working day: ${new Date(relieveDate).toLocaleDateString()}`);
      setShowRelieveDialog(false);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set notice period');
    } finally {
      setLoading(false);
    }
  };

  // Relieve Staff Immediately
  const handleRelieveStaff = async () => {
    if (!selectedStaff) return;
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/status`, {
        status: 'relieved',
        date_of_relieving: relieveDate || new Date().toISOString().split('T')[0]
      }, { headers: getAuthHeaders() });
      toast.success('Staff relieved immediately');
      setShowRelieveDialog(false);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to relieve staff');
    } finally {
      setLoading(false);
    }
  };

  // Cancel Notice Period (reactivate)
  const handleCancelNoticePeriod = async () => {
    if (!selectedStaff) return;
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}/status`, {
        status: 'active'
      }, { headers: getAuthHeaders() });
      toast.success('Notice period cancelled. Staff is active again.');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to cancel notice period');
    } finally {
      setLoading(false);
    }
  };

  // Delete Staff
  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to permanently delete this staff? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`, { headers: getAuthHeaders() });
      toast.success('Staff deleted');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  // Generate Payroll
  const handleGeneratePayroll = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payroll/generate`, {
        franchise_id: franchiseId,
        month: payrollMonth,
        year: payrollYear
      }, { headers: getAuthHeaders() });
      
      setPayrollData(response.data);
      toast.success(`Payroll generated for ${response.data.staff_count} staff`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payroll');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const openSetupDialog = (staff, step) => {
    setSelectedStaff(staff);
    
    if (step === 'shift') {
      setShiftForm({
        shift_type: staff.shift_type || 'fixed',
        shift_start_time: staff.shift_start_time || '09:00',
        shift_end_time: staff.shift_end_time || '18:00',
        grace_minutes: staff.grace_minutes || 10,
        required_hours_per_day: staff.required_hours_per_day || 8
      });
      setShowShiftDialog(true);
    } else if (step === 'salary') {
      setSalaryForm({
        salary_type: staff.salary_type || 'monthly',
        salary_amount: staff.salary_amount?.toString() || '',
        overtime_allowed: staff.overtime_allowed || false,
        ot_multiplier: staff.ot_multiplier || 1.0
      });
      setShowSalaryDialog(true);
    } else if (step === 'face') {
      setShowFaceDialog(true);
    }
  };

  const toggleStaffExpand = async (staff) => {
    if (expandedStaff === staff.staff_id) {
      setExpandedStaff(null);
      setAttendanceData([]);
      setSalaryData(null);
    } else {
      setExpandedStaff(staff.staff_id);
      setSelectedStaff(staff);
      await fetchStaffAttendance(staff.staff_id);
      await fetchStaffSalary(staff.staff_id);
    }
  };

  // Filter staff
  const filteredStaff = staffList.filter(staff => {
    const matchesSearch = staff.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         staff.phone_number?.includes(searchQuery);
    const effectiveStatus = staff.effective_status || staff.status || (staff.is_active ? 'active' : 'draft');
    const matchesStatus = statusFilter === 'all' || 
                         effectiveStatus === statusFilter ||
                         (statusFilter === 'active' && effectiveStatus === 'notice_period'); // Show notice period staff in active filter
    return matchesSearch && matchesStatus;
  });

  // Group attendance by date
  const groupedAttendance = attendanceData.reduce((acc, record) => {
    if (!acc[record.date]) acc[record.date] = [];
    acc[record.date].push(record);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button onClick={() => setShowPayrollDialog(true)} variant="outline" size="sm">
          <CircleDollarSign className="h-4 w-4 mr-1" /> Payroll
        </Button>
        <Button onClick={() => { setBasicForm({ full_name: '', phone_number: '', role: 'Cashier', age: '', sex: 'Male', aadhaar_number: '', address: '', city: '', pincode: '', date_of_joining: '' }); setShowAddDialog(true); }} className="bg-primary text-white">
          <Plus className="h-4 w-4 mr-1" /> Add Staff
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Search by name or phone..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="pl-10" 
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="notice_period">Notice Period</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="relieved">Relieved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Staff List */}
      <div className="space-y-3">
        {loading && staffList.length === 0 ? (
          <Card><CardContent className="py-8 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" /></CardContent></Card>
        ) : filteredStaff.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No staff members found</p>
              <Button onClick={() => setShowAddDialog(true)} className="mt-4" variant="outline">
                <UserPlus className="h-4 w-4 mr-2" /> Add Your First Staff
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredStaff.map((staff) => (
            <Card key={staff.staff_id} className="overflow-hidden">
              <CardContent className="p-0">
                {/* Staff Row Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleStaffExpand(staff)}
                >
                  <div className="flex items-center gap-3">
                    {staff.profile_photo ? (
                      <img src={staff.profile_photo} alt={staff.full_name} className="w-10 h-10 rounded-full object-cover border-2 border-green-500" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${staff.face_enrolled || staff.face_descriptor ? 'bg-green-500' : 'bg-gray-400'}`}>
                        {staff.full_name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-primary">{staff.full_name}</p>
                        <StatusBadge 
                          status={staff.effective_status || staff.status || (staff.is_active ? 'active' : 'draft')} 
                          dateOfRelieving={staff.date_of_relieving}
                          daysUntilRelieve={staff.days_until_relieve}
                          lastWorkingDate={staff.last_working_date}
                        />
                      </div>
                      <p className="text-sm text-gray-500">{staff.role || 'Staff'} • {staff.phone_number}</p>
                      {(staff.effective_status === 'draft' || staff.status === 'draft') && <SetupProgress staff={staff} />}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Quick action buttons for draft staff */}
                    {(staff.effective_status === 'draft' || staff.status === 'draft') && (
                      <>
                        {!staff.shift_configured && (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openSetupDialog(staff, 'shift'); }}>
                            <Clock className="h-3 w-3 mr-1" /> Shift
                          </Button>
                        )}
                        {staff.shift_configured && !staff.payroll_configured && (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openSetupDialog(staff, 'salary'); }}>
                            <DollarSign className="h-3 w-3 mr-1" /> Salary
                          </Button>
                        )}
                        {staff.payroll_configured && !staff.face_enrolled && (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openSetupDialog(staff, 'face'); }}>
                            <ScanFace className="h-3 w-3 mr-1" /> Face
                          </Button>
                        )}
                        {staff.shift_configured && staff.payroll_configured && staff.face_enrolled && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={(e) => { e.stopPropagation(); setSelectedStaff(staff); handleActivateStaff(); }}>
                            <Play className="h-3 w-3 mr-1" /> Activate
                          </Button>
                        )}
                      </>
                    )}
                    {expandedStaff === staff.staff_id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedStaff === staff.staff_id && (
                  <div className="border-t bg-muted/30 p-4">
                    <Tabs defaultValue="details" className="w-full">
                      <TabsList className="mb-4">
                        <TabsTrigger value="details">Details</TabsTrigger>
                        <TabsTrigger value="attendance">Attendance</TabsTrigger>
                        <TabsTrigger value="salary">Salary</TabsTrigger>
                        <TabsTrigger value="actions">Actions</TabsTrigger>
                      </TabsList>

                      {/* Details Tab */}
                      <TabsContent value="details" className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div className="bg-white p-3 rounded-lg">
                            <p className="text-gray-500 text-xs">Role</p>
                            <p className="font-medium">{staff.role || 'Staff'}</p>
                          </div>
                          <div className="bg-white p-3 rounded-lg">
                            <p className="text-gray-500 text-xs">Joining Date</p>
                            <p className="font-medium">{staff.date_of_joining || 'Not set'}</p>
                          </div>
                          <div className="bg-white p-3 rounded-lg">
                            <p className="text-gray-500 text-xs">Shift Type</p>
                            <p className="font-medium capitalize">{staff.shift_type || 'Not configured'}</p>
                          </div>
                          <div className="bg-white p-3 rounded-lg">
                            <p className="text-gray-500 text-xs">Salary Type</p>
                            <p className="font-medium capitalize">{staff.salary_type || 'Not configured'}</p>
                          </div>
                        </div>
                        
                        {staff.shift_type === 'fixed' && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-blue-800">Fixed Shift</p>
                            <p className="text-sm text-blue-600">
                              {staff.shift_start_time} - {staff.shift_end_time} (Grace: {staff.grace_minutes} min)
                            </p>
                          </div>
                        )}
                        
                        {staff.shift_type === 'flexible' && (
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-purple-800">Flexible Shift</p>
                            <p className="text-sm text-purple-600">
                              Required: {staff.required_hours_per_day} hours/day
                            </p>
                          </div>
                        )}
                        
                        {staff.salary_amount > 0 && (
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-green-800">Salary</p>
                            <p className="text-lg font-bold text-green-700">
                              ₹{staff.salary_amount?.toLocaleString()} 
                              <span className="text-sm font-normal">/{staff.salary_type === 'monthly' ? 'month' : staff.salary_type === 'daily' ? 'day' : 'hour'}</span>
                            </p>
                            {staff.overtime_allowed && (
                              <p className="text-xs text-green-600">OT: {staff.ot_multiplier}x rate</p>
                            )}
                          </div>
                        )}
                      </TabsContent>

                      {/* Attendance Tab */}
                      <TabsContent value="attendance" className="space-y-4">
                        <div className="flex flex-wrap gap-2 items-end">
                          <div>
                            <Label className="text-xs">From</Label>
                            <Input type="date" value={dateRange.start_date} onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))} className="w-36" />
                          </div>
                          <div>
                            <Label className="text-xs">To</Label>
                            <Input type="date" value={dateRange.end_date} onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))} className="w-36" />
                          </div>
                          <Button size="sm" onClick={() => { fetchStaffAttendance(staff.staff_id); fetchStaffSalary(staff.staff_id); }}>
                            Filter
                          </Button>
                        </div>

                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {Object.keys(groupedAttendance).length === 0 ? (
                            <p className="text-sm text-gray-500 py-4 text-center">No attendance records</p>
                          ) : (
                            Object.entries(groupedAttendance).sort(([a], [b]) => b.localeCompare(a)).map(([date, records]) => (
                              <div key={date} className="bg-white rounded p-2 text-sm">
                                <p className="font-medium text-primary mb-1">{date}</p>
                                <div className="flex flex-wrap gap-2">
                                  {records.sort((a, b) => a.punch_time?.localeCompare(b.punch_time)).map((record, idx) => (
                                    <span key={idx} className={`px-2 py-1 rounded text-xs ${record.punch_type === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      <Clock className="h-3 w-3 inline mr-1" />
                                      {record.punch_type?.toUpperCase()}: {record.time}
                                      {record.manually_edited && <span className="ml-1 text-yellow-600">*</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </TabsContent>

                      {/* Salary Tab */}
                      <TabsContent value="salary" className="space-y-4">
                        {salaryData ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3 text-center">
                              <div className="bg-green-100 rounded-lg p-3">
                                <p className="text-2xl font-bold text-green-700">{salaryData.summary?.days_present || 0}</p>
                                <p className="text-xs text-green-600">Present</p>
                              </div>
                              <div className="bg-red-100 rounded-lg p-3">
                                <p className="text-2xl font-bold text-red-700">{salaryData.summary?.days_absent || 0}</p>
                                <p className="text-xs text-red-600">Absent</p>
                              </div>
                              <div className="bg-blue-100 rounded-lg p-3">
                                <p className="text-2xl font-bold text-blue-700">{salaryData.summary?.total_ot_hours || 0}h</p>
                                <p className="text-xs text-blue-600">Overtime</p>
                              </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-600">Gross Salary</span>
                                <span className="font-medium">₹{salaryData.calculation?.base_salary?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center mb-2 text-blue-600">
                                <span>+ Overtime</span>
                                <span>₹{salaryData.calculation?.ot_amount?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center mb-2 text-orange-600">
                                <span>- Deductions</span>
                                <span>₹{salaryData.calculation?.fine_deduction?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between items-center pt-2 border-t">
                                <span className="font-semibold">Final Salary</span>
                                <span className="text-xl font-bold text-green-700">₹{salaryData.calculation?.final_salary?.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 py-4 text-center">Loading salary data...</p>
                        )}
                      </TabsContent>

                      {/* Actions Tab */}
                      <TabsContent value="actions" className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <Button variant="outline" size="sm" onClick={() => openSetupDialog(staff, 'shift')}>
                            <Clock className="h-4 w-4 mr-2" /> Edit Shift
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openSetupDialog(staff, 'salary')}>
                            <DollarSign className="h-4 w-4 mr-2" /> Edit Salary
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openSetupDialog(staff, 'face')}>
                            <ScanFace className="h-4 w-4 mr-2" /> {staff.face_enrolled ? 'Re-enroll' : 'Enroll'} Face
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setSelectedStaff(staff); setShowEditDialog(true); }}>
                            <Edit className="h-4 w-4 mr-2" /> Edit Info
                          </Button>
                        </div>

                        {/* Notice Period Info */}
                        {(staff.effective_status === 'notice_period' || staff.is_in_notice_period) && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-orange-700 font-medium">
                              <AlertCircle className="h-4 w-4" />
                              Notice Period Active
                            </div>
                            <p className="text-sm text-orange-600 mt-1">
                              Relieve Date: {staff.date_of_relieving}<br/>
                              Last Working Day: {staff.last_working_date}<br/>
                              Days Remaining: {staff.days_until_relieve}
                            </p>
                          </div>
                        )}

                        <div className="border-t pt-3 space-y-2">
                          {(staff.effective_status === 'active' || staff.status === 'active') && !staff.is_in_notice_period && (
                            <Button variant="outline" className="w-full text-yellow-600 border-yellow-300" onClick={() => { setSelectedStaff(staff); handleDeactivateStaff(); }}>
                              <PowerOff className="h-4 w-4 mr-2" /> Deactivate Staff
                            </Button>
                          )}
                          {(staff.effective_status === 'inactive' || staff.status === 'inactive') && (
                            <Button variant="outline" className="w-full text-green-600 border-green-300" onClick={() => { setSelectedStaff(staff); handleActivateStaff(); }}>
                              <Power className="h-4 w-4 mr-2" /> Reactivate Staff
                            </Button>
                          )}
                          {/* Cancel Notice Period button for staff in notice period */}
                          {(staff.effective_status === 'notice_period' || staff.is_in_notice_period) && (
                            <Button variant="outline" className="w-full text-green-600 border-green-300" onClick={() => { setSelectedStaff(staff); handleCancelNoticePeriod(); }}>
                              <Power className="h-4 w-4 mr-2" /> Cancel Notice & Reactivate
                            </Button>
                          )}
                          {/* Set Notice Period or Relieve button */}
                          {staff.effective_status !== 'relieved' && staff.status !== 'relieved' && !staff.is_in_notice_period && (
                            <Button variant="outline" className="w-full text-orange-600 border-orange-300" onClick={() => { setSelectedStaff(staff); setRelieveDate(new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]); setShowRelieveDialog(true); }}>
                              <Calendar className="h-4 w-4 mr-2" /> Set Notice Period / Relieve
                            </Button>
                          )}
                          {/* Relieve Now button for staff in notice period */}
                          {(staff.effective_status === 'notice_period' || staff.is_in_notice_period) && (
                            <Button variant="outline" className="w-full text-red-600 border-red-300" onClick={() => { setSelectedStaff(staff); handleRelieveStaff(); }}>
                              <UserX className="h-4 w-4 mr-2" /> Relieve Now
                            </Button>
                          )}
                          <Button variant="outline" className="w-full text-red-600 border-red-300" onClick={() => handleDeleteStaff(staff.staff_id)}>
                            <Trash2 className="h-4 w-4 mr-2" /> Delete Permanently
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Staff Dialog (Step 1) */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Add New Staff
            </DialogTitle>
            <DialogDescription>Step 1: Enter basic information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label>Full Name *</Label>
                <Input value={basicForm.full_name} onChange={(e) => setBasicForm(p => ({ ...p, full_name: e.target.value }))} required />
              </div>
              <div>
                <Label>Mobile Number *</Label>
                <Input value={basicForm.phone_number} onChange={(e) => setBasicForm(p => ({ ...p, phone_number: e.target.value }))} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Role</Label>
                  <Select value={basicForm.role} onValueChange={(v) => setBasicForm(p => ({ ...p, role: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {configOptions?.roles?.map(role => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Joining</Label>
                  <Input type="date" value={basicForm.date_of_joining} onChange={(e) => setBasicForm(p => ({ ...p, date_of_joining: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Age</Label>
                  <Input type="number" value={basicForm.age} onChange={(e) => setBasicForm(p => ({ ...p, age: e.target.value }))} />
                </div>
                <div>
                  <Label>Sex</Label>
                  <Select value={basicForm.sex} onValueChange={(v) => setBasicForm(p => ({ ...p, sex: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create & Continue'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shift Config Dialog (Step 2) */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" /> Configure Shift
            </DialogTitle>
            <DialogDescription>Step 2: Set working hours for {selectedStaff?.full_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfigureShift} className="space-y-4">
            <div>
              <Label>Shift Type *</Label>
              <Select value={shiftForm.shift_type} onValueChange={(v) => setShiftForm(p => ({ ...p, shift_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Shift (set start/end time)</SelectItem>
                  <SelectItem value="flexible">Flexible Shift (set required hours)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {shiftForm.shift_type === 'fixed' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Start Time *</Label>
                    <Input type="time" value={shiftForm.shift_start_time} onChange={(e) => setShiftForm(p => ({ ...p, shift_start_time: e.target.value }))} required />
                  </div>
                  <div>
                    <Label>End Time *</Label>
                    <Input type="time" value={shiftForm.shift_end_time} onChange={(e) => setShiftForm(p => ({ ...p, shift_end_time: e.target.value }))} required />
                  </div>
                </div>
                <div>
                  <Label>Grace Period (minutes)</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    max={calculateWorkingMinutes(shiftForm.shift_start_time, shiftForm.shift_end_time) - 1 || 540}
                    value={shiftForm.grace_minutes === '' ? '' : shiftForm.grace_minutes} 
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setShiftForm(p => ({ ...p, grace_minutes: '' }));
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 0) {
                          setShiftForm(p => ({ ...p, grace_minutes: num }));
                        }
                      }
                    }}
                    placeholder="Enter grace period"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Late arrival within grace period won't be penalized. 
                    Must be between 1 and {calculateWorkingMinutes(shiftForm.shift_start_time, shiftForm.shift_end_time) - 1 || 540} minutes.
                  </p>
                </div>
              </>
            )}

            {shiftForm.shift_type === 'flexible' && (
              <div>
                <Label>Required Hours Per Day *</Label>
                <Input 
                  type="number" 
                  step="0.5" 
                  min="0.5" 
                  max="24" 
                  value={shiftForm.required_hours_per_day === '' ? '' : shiftForm.required_hours_per_day} 
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setShiftForm(p => ({ ...p, required_hours_per_day: '' }));
                    } else {
                      const num = parseFloat(val);
                      if (!isNaN(num) && num >= 0) {
                        setShiftForm(p => ({ ...p, required_hours_per_day: num }));
                      }
                    }
                  }}
                  placeholder="Enter required hours"
                  required 
                />
                <p className="text-xs text-gray-500 mt-1">Must be greater than 0 and up to 24 hours</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowShiftDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & Continue'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Salary Config Dialog (Step 3) */}
      <Dialog open={showSalaryDialog} onOpenChange={setShowSalaryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Configure Salary
            </DialogTitle>
            <DialogDescription>Step 3: Set pay structure for {selectedStaff?.full_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfigureSalary} className="space-y-4">
            <div>
              <Label>Salary Type *</Label>
              <Select value={salaryForm.salary_type} onValueChange={(v) => setSalaryForm(p => ({ ...p, salary_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly Salary</SelectItem>
                  <SelectItem value="daily">Daily Wage</SelectItem>
                  <SelectItem value="hourly">Hourly Wage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount (₹) *</Label>
              <Input 
                type="number" 
                min="0" 
                value={salaryForm.salary_amount} 
                onChange={(e) => setSalaryForm(p => ({ ...p, salary_amount: e.target.value }))} 
                placeholder={salaryForm.salary_type === 'monthly' ? 'e.g., 15000' : salaryForm.salary_type === 'daily' ? 'e.g., 500' : 'e.g., 100'}
                required 
              />
              <p className="text-xs text-gray-500 mt-1">
                {salaryForm.salary_type === 'monthly' && 'Per month salary'}
                {salaryForm.salary_type === 'daily' && 'Per day wage'}
                {salaryForm.salary_type === 'hourly' && 'Per hour wage'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="ot_allowed"
                checked={salaryForm.overtime_allowed} 
                onChange={(e) => setSalaryForm(p => ({ ...p, overtime_allowed: e.target.checked }))}
                className="w-4 h-4" 
              />
              <Label htmlFor="ot_allowed" className="cursor-pointer">Allow Overtime Pay</Label>
            </div>

            {salaryForm.overtime_allowed && (
              <div>
                <Label>OT Multiplier</Label>
                <Select value={salaryForm.ot_multiplier.toString()} onValueChange={(v) => setSalaryForm(p => ({ ...p, ot_multiplier: parseFloat(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {configOptions?.ot_multipliers?.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label} rate</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSalaryDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & Continue'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Face Enrollment Dialog (Step 4) */}
      <Dialog open={showFaceDialog} onOpenChange={setShowFaceDialog}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <FaceCapture 
            mode="register" 
            onCapture={handleFaceCapture} 
            onCancel={() => setShowFaceDialog(false)} 
          />
        </DialogContent>
      </Dialog>

      {/* Relieve Staff / Notice Period Dialog */}
      <Dialog open={showRelieveDialog} onOpenChange={setShowRelieveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Calendar className="h-5 w-5" /> Set Relieve Date
            </DialogTitle>
            <DialogDescription>
              Set when {selectedStaff?.full_name} will be relieved. They can mark attendance until the day before the relieve date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Relieve Date *</Label>
              <Input 
                type="date" 
                value={relieveDate} 
                onChange={(e) => setRelieveDate(e.target.value)}
                min={new Date(Date.now() + 24*60*60*1000).toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Staff can mark attendance until {relieveDate ? new Date(new Date(relieveDate).getTime() - 24*60*60*1000).toLocaleDateString() : 'the day before'}
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800 font-medium">Notice Period Benefits:</p>
              <ul className="text-xs text-orange-700 mt-1 space-y-1 list-disc list-inside">
                <li>Staff can still mark attendance until last working day</li>
                <li>Attendance data is captured for payroll</li>
                <li>Status shows "Notice Period" with countdown</li>
                <li>You can cancel notice period anytime before relieve date</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={() => setShowRelieveDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSetNoticePeriod} 
              disabled={loading || !relieveDate} 
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Notice Period'}
            </Button>
            <Button 
              onClick={handleRelieveStaff} 
              disabled={loading} 
              variant="destructive"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Relieve Immediately'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Dialog */}
      <Dialog open={showPayrollDialog} onOpenChange={setShowPayrollDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5" /> Payroll Management
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-end gap-3">
              <div>
                <Label>Month</Label>
                <Select value={payrollMonth.toString()} onValueChange={(v) => setPayrollMonth(parseInt(v))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select value={payrollYear.toString()} onValueChange={(v) => setPayrollYear(parseInt(v))}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleGeneratePayroll} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Generate Payroll
              </Button>
            </div>

            {payrollData && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-green-600">Total Payable</p>
                      <p className="text-2xl font-bold text-green-700">₹{payrollData.total_payable?.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-600">Staff Count</p>
                      <p className="text-xl font-bold text-green-700">{payrollData.staff_count}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {payrollData.payrolls?.map(p => (
                    <div key={p.payroll_id} className="bg-white p-3 rounded-lg border flex justify-between items-center">
                      <div>
                        <p className="font-medium">{p.staff_name}</p>
                        <p className="text-xs text-gray-500">
                          Present: {p.days_present} | Absent: {p.days_absent} | OT: {Math.round(p.total_ot_minutes / 60)}h
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-700">₹{p.final_salary?.toLocaleString()}</p>
                        <Badge variant={p.is_locked ? 'default' : 'outline'} className="text-xs">
                          {p.is_locked ? 'Locked' : 'Draft'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagementV2;
